import { Button, CheckIcon, Dialog, DialogButtons, Select, Spinner } from '@st/theme'
import { formatMoney } from '@st/util/format'
import { ReactNode, useState } from 'react'
import { CURRENT_PROMOTION, NUMBER_OF_CREDITS, Promotion } from './config'
import { useOrganizationBilling } from './hooks'
import { startPurchaseCreditsCheckoutFlow } from './organization-billing-module'

type Props = {
  organizationId: string
  continueUrl?: string
  onClose: () => void
}
export function PurchaseCreditsDialog({ organizationId, continueUrl, onClose }: Props) {
  const [state, send] = useOrganizationBilling(organizationId)

  if (state.status != 'loaded') {
    return null
  }

  return (
    <PurchaseCreditsDialogView
      onClose={onClose}
      creditsUnitPrice={state.billingState.creditsUnitPrice}
      creditOptions={NUMBER_OF_CREDITS}
      promotion={CURRENT_PROMOTION}
      isGeneratingCheckoutLink={state.generatingCheckoutLink}
      onContinue={(quantity) => {
        send(
          startPurchaseCreditsCheckoutFlow({
            quantity,
            continueUrl: continueUrl ?? window.location.href
          })
        )
      }}
    />
  )
}

type PurchaseCreditsDialogProps = {
  creditsUnitPrice: number
  creditOptions: number[]
  promotion: Promotion | undefined
  onContinue: (quantity: number) => void
  onClose: () => void
  isGeneratingCheckoutLink: boolean
}

function PurchaseCreditsDialogView({
  creditsUnitPrice,
  creditOptions,
  promotion,
  onContinue,
  onClose,
  isGeneratingCheckoutLink
}: PurchaseCreditsDialogProps) {
  const [selectedCreditQuantity, setSelectedCreditQuantity] = useState<number | undefined>()

  return (
    <Dialog
      title="Purchase credits"
      subtitle={
        <span>
          {`Please select the number of credits you'd like to purchase. Each return costs one credit. Unused credits will
          roll over to the following year.`}
        </span>
      }
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={isGeneratingCheckoutLink ? <Spinner className="size-4" /> : undefined}
            disabled={selectedCreditQuantity == undefined || isGeneratingCheckoutLink}
            onClick={() => {
              if (selectedCreditQuantity != undefined) {
                onContinue(selectedCreditQuantity)
              }
            }}
          >
            Continue
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col items-start p-2">
        <Select
          placeholder="Select number of credits"
          options={creditOptions}
          value={selectedCreditQuantity}
          onChange={(quantity) => setSelectedCreditQuantity(quantity as number)}
          buildValue={(quantity: number) => quantity}
          buildLabel={(quantity: number) => `${quantity} credits`}
        />
        {selectedCreditQuantity !== undefined && (
          <ul className="mt-4 space-y-1 text-sm/6 text-gray-600">
            {promotion ? (
              <PurchaseSummaryItem>
                <span className="line-through">
                  {formatMoney(selectedCreditQuantity * creditsUnitPrice)}
                </span>{' '}
                {formatMoney(selectedCreditQuantity * creditsUnitPrice * (1 - promotion.discount))}{' '}
                before {promotion.before} (code: {promotion.coupon})
              </PurchaseSummaryItem>
            ) : (
              <PurchaseSummaryItem>
                Total price: {formatMoney(selectedCreditQuantity * creditsUnitPrice)}
              </PurchaseSummaryItem>
            )}
            <PurchaseSummaryItem>No implementation fees</PurchaseSummaryItem>
            <PurchaseSummaryItem>
              Unused credits roll over to the following year
            </PurchaseSummaryItem>
          </ul>
        )}
      </div>
    </Dialog>
  )
}

function PurchaseSummaryItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-x-3 text-base">
      <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
      {children}
    </li>
  )
}
